/**
 * Generic ErrorBoundary component for handling React component errors
 * Provides configurable error handling with recovery options
 */

import {
  RiArrowLeftLine,
  RiErrorWarningLine,
  RiHomeLine,
  RiRefreshLine,
  RiServerLine,
  RiShieldCheckLine,
  RiTimeLine,
  RiWifiOffLine,
} from '@remixicon/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import { Card, CardContent, CardHeader, CardTitle } from '../card';

const CHUNK_RELOAD_FLAG = 'chunk_reload_retry';
const CHUNK_RELOAD_PARAM = '__chunk_reload';

// Error boundary state interface
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | undefined;
  errorId: string;
}

// Error boundary props
export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
  featureName?: string; // Name of the feature for better error messages
  showRetry?: boolean; // Show retry button
  showGoHome?: boolean; // Show go home button
  enableAutoRetry?: boolean; // Enable auto-retry after timeout
  autoRetryDelay?: number; // Auto-retry delay in ms (default: 5000)
  className?: string; // Additional CSS classes
}

// Error fallback props
export interface ErrorFallbackProps {
  error: Error;
  errorInfo?: ErrorInfo;
  resetError: () => void;
  resetKeys?: Array<string | number>;
  featureName?: string;
  showRetry?: boolean;
  showGoHome?: boolean;
  className?: string;
}

// Home error fallback props for app-level errors
export interface ShellErrorFallbackProps {
  error: Error;
  resetError?: () => void; // Optional since it's not used
  appName?: string;
}

/**
 * Main generic error boundary component
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: number | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: undefined,
      errorId: '',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Generate unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    if (isChunkLoadError(error)) {
      if (safeChunkReload()) {
        return;
      }
    }

    // Log error for monitoring
    console.error(
      `Error Boundary caught an error${this.props.featureName ? ` in ${this.props.featureName}` : ''}:`,
      {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        errorId: this.state.errorId,
        featureName: this.props.featureName,
      }
    );

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Send to error tracking service (if configured)
    this.reportError(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    if (hasError && prevProps.resetKeys !== resetKeys) {
      if (resetOnPropsChange || resetKeys?.some((key, i) => key !== prevProps.resetKeys?.[i])) {
        this.resetError();
      }
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private reportError = (_error: Error, _errorInfo: ErrorInfo) => {
    // In production, send to error tracking service
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry, LogRocket, Bugsnag, etc.
      // errorTrackingService.captureException(error, {
      //   extra: { errorInfo, featureName: this.props.featureName },
      //   tags: { component: this.props.featureName || 'unknown' }
      // });
    }
  };

  private resetError = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: undefined,
      errorId: '',
    });
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback: Fallback } = this.props;

    if (hasError && error) {
      if (Fallback) {
        return (
          <Fallback
            error={error}
            errorInfo={errorInfo}
            resetError={this.resetError}
            featureName={this.props.featureName}
            showRetry={this.props.showRetry}
            showGoHome={this.props.showGoHome}
            className={this.props.className}
          />
        );
      }

      return (
        <ErrorFallback
          error={error}
          errorInfo={errorInfo}
          resetError={this.resetError}
          featureName={this.props.featureName}
          showRetry={this.props.showRetry !== false}
          showGoHome={this.props.showGoHome}
          className={this.props.className}
        />
      );
    }

    return children;
  }
}

function reloadWithCacheBust() {
  const url = new URL(window.location.href);
  url.searchParams.set(CHUNK_RELOAD_PARAM, Date.now().toString());
  window.location.replace(url.toString());
}

function safeChunkReload() {
  const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_FLAG);
  if (hasReloaded) return false;

  sessionStorage.setItem(CHUNK_RELOAD_FLAG, 'true');
  console.warn(
    '[ErrorBoundary] Dynamic module import failure detected. Auto-reloading page with cache bust...'
  );
  reloadWithCacheBust();
  return true;
}

function forceChunkReload() {
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, 'true');
  reloadWithCacheBust();
}

/**
 * Utility function to detect dynamic module chunk import errors.
 * Handles 404s, MIME type mismatches (index.html fallback), and React.lazy undefined resolution.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = typeof error === 'string' ? error : (error as Error)?.message || String(error || '');
  const msgLower = msg.toLowerCase();

  return (
    msgLower.includes('failed to fetch dynamically imported module') ||
    msgLower.includes('error loading dynamically imported module') ||
    msgLower.includes('importing a module script failed') ||
    msgLower.includes('loading chunk') ||
    msgLower.includes('failed to load module script') ||
    msgLower.includes('expected a javascript-or-wasm module script') ||
    msgLower.includes('mime type') ||
    msgLower.includes("reading 'default'") ||
    msgLower.includes('reading default') ||
    msgLower.includes("property 'default'")
  );
}

/**
 * Default error fallback component with recovery options
 */
export function ErrorFallback({
  error,
  resetError,
  featureName,
  showRetry = true,
  showGoHome = false,
  className = '',
}: ErrorFallbackProps) {
  const getErrorType = (error: Error) => {
    if (isChunkLoadError(error)) {
      return 'chunk_load';
    }
    const message = error.message.toLowerCase();
    if (message.includes('network') || message.includes('fetch')) {
      return 'network';
    }
    if (message.includes('unauthorized') || message.includes('403') || message.includes('401')) {
      return 'auth';
    }
    if (message.includes('500') || message.includes('server')) {
      return 'server';
    }
    if (message.includes('timeout')) {
      return 'timeout';
    }
    return 'unknown';
  };

  const errorType = getErrorType(error);

  const getErrorIcon = () => {
    switch (errorType) {
      case 'chunk_load':
        return <RiRefreshLine className="h-12 w-12 text-blue-500" />;
      case 'network':
        return <RiWifiOffLine className="h-12 w-12 text-red-500" />;
      case 'server':
        return <RiServerLine className="h-12 w-12 text-red-500" />;
      case 'timeout':
        return <RiTimeLine className="h-12 w-12 text-yellow-500" />;
      case 'auth':
        return <RiShieldCheckLine className="h-12 w-12 text-orange-500" />;
      default:
        return <RiErrorWarningLine className="h-12 w-12 text-red-500" />;
    }
  };

  const getErrorMessage = () => {
    const feature = featureName || 'this feature';

    switch (errorType) {
      case 'chunk_load':
        return {
          title: 'New Version Available',
          description: `A new version of ${feature} has been deployed. Please reload the page to load the latest application files.`,
          suggestion: 'Click below to reload the application.',
        };
      case 'network':
        return {
          title: 'Connection Problem',
          description: `Unable to connect to the server to load ${feature}. Please check your internet connection and try again.`,
          suggestion: 'Check your network connection or try refreshing the page.',
        };
      case 'server':
        return {
          title: 'Server Error',
          description: `Something went wrong on our end while loading ${feature}. Our team has been notified and is working on a fix.`,
          suggestion:
            'Please try again in a few minutes, or contact support if the problem persists.',
        };
      case 'timeout':
        return {
          title: 'Request Timeout',
          description: `The request to load ${feature} took too long to complete. This might be due to a slow connection or high server load.`,
          suggestion: 'Try again with a more specific request or check your connection.',
        };
      case 'auth':
        return {
          title: 'Authentication Required',
          description:
            "Your session has expired or you don't have permission to access this resource.",
          suggestion: 'Please log in again or contact your administrator for access.',
        };
      default:
        return {
          title: 'Something Went Wrong',
          description: `An unexpected error occurred while loading ${feature}.`,
          suggestion: 'Try refreshing the page or contact support if the problem continues.',
        };
    }
  };

  const errorDetails = getErrorMessage();

  const handleRetry = () => {
    if (errorType === 'chunk_load') {
      forceChunkReload();
      return;
    }
    resetError();
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className={cn('flex items-center justify-center min-h-[400px] p-6', className)}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">{getErrorIcon()}</div>
          <CardTitle className="text-xl font-semibold">{errorDetails.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">{errorDetails.description}</p>
            <p className="text-xs text-muted-foreground">{errorDetails.suggestion}</p>
          </div>

          {/* Error details (only in development) */}
          {process.env.NODE_ENV === 'development' && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Technical Details
              </summary>
              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-h-32">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}

          <div className="flex flex-col gap-2">
            {showRetry && (
              <Button onClick={handleRetry} className="w-full">
                <RiRefreshLine className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            )}

            <Button variant="outline" onClick={handleGoBack} className="w-full">
              <RiArrowLeftLine className="h-4 w-4 mr-2" />
              Go Back
            </Button>

            {showGoHome && (
              <Button variant="outline" onClick={handleGoHome} className="w-full">
                <RiHomeLine className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            )}
          </div>

          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              Error ID: <code className="bg-muted px-1 rounded">{Date.now()}</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Home-level error fallback for critical app errors
 */
export function ShellErrorFallback({ error, appName = 'Application' }: ShellErrorFallbackProps) {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <RiErrorWarningLine className="h-16 w-16 text-red-500" />
          </div>
          <CardTitle className="text-2xl font-bold">{appName} Error</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              A critical error has occurred in {appName.toLowerCase()}.
            </p>
            <p className="text-xs text-muted-foreground">
              We apologize for the inconvenience. Please reload the page to continue.
            </p>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Error Details
              </summary>
              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-h-32">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}

          <div className="flex flex-col gap-2">
            <Button onClick={handleReload} className="w-full">
              <RiRefreshLine className="h-4 w-4 mr-2" />
              Reload Application
            </Button>
          </div>

          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              If this problem persists, please contact support.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Export error types for consistent error handling
export const ErrorTypes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  PERMISSION_ERROR: 'PERMISSION_ERROR',
  NOT_FOUND_ERROR: 'NOT_FOUND_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorType = (typeof ErrorTypes)[keyof typeof ErrorTypes];
